import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api, Order } from '@/lib/api';
import { ArrowRight, Phone, MapPin, Navigation, ExternalLink, Package, Truck, CheckCircle2, Search } from 'lucide-react';
import L from 'leaflet';

export default function DriverOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [driverPos, setDriverPos] = useState<[number, number] | null>(null);
  const [destPos, setDestPos] = useState<[number, number] | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const routeLayerRef = useRef<L.Polyline | null>(null);
  const driverMarkerRef = useRef<L.Marker | null>(null);
  const destMarkerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!session || session.role !== 'driver') {
      navigate('/driver/login', { replace: true });
      return;
    }
    fetchOrder();
  }, [id, session, authLoading]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [24.7136, 46.6753],
      zoom: 12,
      zoomControl: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    mapInstanceRef.current = map;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setDriverPos(coords);
        map.setView(coords, 14);
      },
      () => {},
      { enableHighAccuracy: true }
    );

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const coords: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setDriverPos(coords);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000 }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !driverPos) return;
    const map = mapInstanceRef.current;

    const driverIcon = L.divIcon({
      className: '',
      html: '<div style="width:16px;height:16px;background:#2cb1bc;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLatLng(driverPos);
    } else {
      driverMarkerRef.current = L.marker(driverPos, { icon: driverIcon }).addTo(map);
    }
  }, [driverPos]);

  useEffect(() => {
    if (!mapInstanceRef.current || !destPos) return;
    const map = mapInstanceRef.current;

    const destIcon = L.divIcon({
      className: '',
      html: '<div style="width:14px;height:14px;background:#ef4444;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });

    if (destMarkerRef.current) {
      destMarkerRef.current.setLatLng(destPos);
    } else {
      destMarkerRef.current = L.marker(destPos, { icon: destIcon }).addTo(map);
    }

    map.setView(destPos, 15);

    if (driverPos) {
      fetchRoute(driverPos, destPos);
      const bounds = L.latLngBounds([driverPos, destPos]);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [destPos, driverPos]);

  const fetchOrder = async () => {
    if (!session || session.role !== 'driver') return;
    try {
      const data = await api.orders.list(session.token);
      const found = data.orders.find(o => o.id === Number(id));
      if (found) {
        setOrder(found);
        if (found.delivery_address && !destPos) {
          geocodeAddress(found.delivery_address);
        }
      }
    } catch {}
    setLoading(false);
  };

  const geocodeAddress = async (address: string) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await res.json();
      if (data.length > 0) {
        setDestPos([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
      }
    } catch {}
  };

  const fetchRoute = async (from: [number, number], to: [number, number]) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (routeLayerRef.current) {
      map.removeLayer(routeLayerRef.current);
    }

    try {
      const res = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`
      );
      const data = await res.json();
      if (data.routes?.[0]?.geometry) {
        const coords = data.routes[0].geometry.coordinates.map(
          (c: [number, number]) => [c[1], c[0]] as [number, number]
        );
        routeLayerRef.current = L.polyline(coords, {
          color: '#2cb1bc',
          weight: 4,
          opacity: 0.8,
        }).addTo(map);
      }
    } catch {
      routeLayerRef.current = L.polyline([from, to], {
        color: '#2cb1bc',
        weight: 3,
        opacity: 0.6,
        dashArray: '8, 8',
      }).addTo(map);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    await geocodeAddress(searchQuery);
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!session || session.role !== 'driver' || !order) return;
    setUpdating(true);
    try {
      await api.orders.updateStatus(order.id, newStatus, session.token);
      await fetchOrder();
      if (newStatus === 'completed') {
        navigate('/driver');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const openExternalMap = () => {
    if (destPos) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${destPos[0]},${destPos[1]}`, '_blank');
    } else if (order) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.delivery_address)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" dir="rtl">
        <div className="animate-pulse text-cyan-400">جاري التحميل...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" dir="rtl">
        <p className="text-navy-400">الطلب غير موجود</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 flex flex-col" dir="rtl">
      <header className="bg-navy-900/90 backdrop-blur-lg border-b border-navy-800 px-4 py-3 flex items-center gap-3 sticky top-0 z-50">
        <button onClick={() => navigate('/driver')} className="text-navy-400 hover:text-white transition-colors">
          <ArrowRight className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <p className="text-white font-bold text-sm">طلب #{order.order_number}</p>
          <p className="text-navy-400 text-xs">
            {order.status === 'accepted' ? 'جاهز للتوصيل' : order.status === 'in_delivery' ? 'قيد التوصيل' : 'بانتظارك'}
          </p>
        </div>
        {order.amount > 0 && (
          <span className="bg-cyan-700/20 text-cyan-400 text-sm font-bold px-3 py-1 rounded-lg">
            {order.amount.toFixed(2)}
          </span>
        )}
      </header>

      <div className="flex-1 flex flex-col">
        {/* Map */}
        <div className="relative h-64 sm:h-80">
          <div ref={mapRef} className="absolute inset-0 z-0" />
          <div className="absolute top-3 left-3 right-3 z-[1000]">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="ابحث عن عنوان..."
                className="flex-1 bg-navy-900/90 backdrop-blur border border-navy-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-navy-500 focus:outline-none focus:border-cyan-500"
              />
              <button
                onClick={handleSearch}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 rounded-xl transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>
          <button
            onClick={openExternalMap}
            className="absolute bottom-3 left-3 z-[1000] bg-navy-900/90 backdrop-blur border border-navy-600 text-white text-xs px-3 py-2 rounded-xl flex items-center gap-1.5 hover:bg-navy-800 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            فتح في الخرائط
          </button>
        </div>

        {/* Order Info */}
        <div className="flex-1 bg-navy-900 rounded-t-3xl -mt-4 relative z-10 p-5 space-y-4">
          <div className="w-10 h-1 bg-navy-700 rounded-full mx-auto mb-4" />

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-navy-800 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                <Package className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <p className="text-navy-400 text-xs">العميل</p>
                <p className="text-white font-medium">{order.customer_name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <a
                href={`tel:${order.customer_phone}`}
                className="w-9 h-9 bg-green-600/20 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
              >
                <Phone className="w-4 h-4 text-green-400" />
              </a>
              <div>
                <p className="text-navy-400 text-xs">الهاتف</p>
                <a href={`tel:${order.customer_phone}`} className="text-white font-medium" dir="ltr">
                  {order.customer_phone}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-navy-800 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <p className="text-navy-400 text-xs">العنوان</p>
                <p className="text-white">{order.delivery_address}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-4 border-t border-navy-800">
            {order.status === 'pending' && (
              <div className="flex gap-3">
                <button
                  onClick={() => handleUpdateStatus('accepted')}
                  disabled={updating}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-3.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  قبول الطلب
                </button>
                <button
                  onClick={() => handleUpdateStatus('rejected')}
                  disabled={updating}
                  className="bg-navy-700 hover:bg-red-600/30 text-navy-300 hover:text-red-400 px-5 py-3.5 rounded-xl font-medium transition-colors"
                >
                  رفض
                </button>
              </div>
            )}
            {order.status === 'accepted' && (
              <button
                onClick={() => handleUpdateStatus('in_delivery')}
                disabled={updating}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white py-3.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Truck className="w-5 h-5" />
                بدء التوصيل
              </button>
            )}
            {order.status === 'in_delivery' && (
              <button
                onClick={() => handleUpdateStatus('completed')}
                disabled={updating}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-3.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-5 h-5" />
                تم التسليم
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
